<template lang="pug">
    section
        .customer(v-if="form.sold_by !== 'point-of-sale' || (form.purchaser.customer && form.purchaser.customer.id)")
            template(v-if="form.purchaser")
                include customer.pug
        .guest(v-else)
            .alert.warning
                p {{ $t('This purchase was placed as a guest. Would you like to select a customer?') }}
            el-button(
                type="primary" plain
                icon="el-icon-fa-user-plus fas"
                @click="openCustomerDrawer"
            ) {{ $t('Add customer to this purchase') }}

        el-drawer(
            :title="$t('Select Customer')"
            :visible.sync="drawer.visibility"
            :wrapper-closable="false"
            :modal="true"
            size="80%"
            @closed="resetDrawer"
        )
            .purchaser-modal-form.el-form(v-loading="drawer.loading")
                paginated-items(
                    :scope="drawer.scope"
                    :type="drawer.key"
                    :loading="drawer.loading"
                    :items="drawer.membersPaginated.items"
                    :filters="filters"
                    :show-filters="true"
                    :variables="drawer.membersPaginated.variables"
                    :all-columns="drawer.membersPaginated.allColumns"
                    :active-columns="drawer.membersPaginated.activeColumns"
                    :page-info="drawer.membersPaginated.pageInfo"
                    :sortables="drawer.sortables"
                    :sort="sort"
                    @pagination-size-change="handleSizeChange"
                    @pagination-current-change="handleCurrentChange"
                    @show-table-columns="handleColsShow"
                    @change-filter="handleFilterInputChange"
                    @click="toggleSelectedCustomer"
                    @change-sort="handleSortInputChange"
                )
                    template(v-slot:address_country="{ value }") {{ value | country }}
                    template(v-slot:actions="{ item }")
                        .col-left
                            el-radio(
                                :key="item.id"
                                :value="item"
                                :label="drawer.member"
                                @click.native.prevent="event => toggleSelectedCustomer(item)"
                            )
            .drawer-footer
                .left
                    el-button(
                        round
                        size="mini"
                        icon="el-icon-fa-times fas"
                        @click="closeDrawer"
                    ) {{ $t('Dismiss') }}
                .right
                    el-button(
                        round
                        size="mini"
                        :disabled="drawer.loading || ! drawer.member"
                        :loading="drawer.saving"
                        type="primary"
                        icon="el-icon-fa-check fas"
                        @click="savePurchaser"
                    )
                        template(v-if="drawer.member")
                            | {{ $t('Save customer "{customer}" and close', { customer: this.drawer.member.name }) }}
                        template(v-else)
                            | {{ $t('Save and close') }}
</template>

<i18n>
{
    "en": {
        "enabled": "Enabled",
        "disabled": "Disabled",
        "blocked": "Blocked",
        "Save customer \"{customer}\" and close": "Save customer \"{customer}\" and close"
    },
    "de": {
        "Status": "Status",
        "Email": "E-Mail",
        "Phone": "Telefon",
        "enabled": "Aktiv",
        "disabled": "Inaktiv",
        "blocked": "Gesperrt",
        "Save customer \"{customer}\" and close": "Kunde \"{customer}\" speichern und schließen",
        "Person list could not be fetched.": "Personenliste konnte nicht abgerufen werden.",
        "This purchase was placed as a guest. Would you like to select a customer?": "Dieser Kauf wurde als Gast getätigt. Kunden auswählen?",
        "Add customer to this purchase": "Diesem Kauf einen Kunden zuordnen",
        "Customer could not be saved.": "Kunde konnte nicht gespeichert werden.",
        "Customer saved successfully!": "Kunde erfolgreich gespeichert!",
        "Select Customer": "Kunde auswählen",
        "Billing address & shipping address": "Rechnungs- & Versandadresse",
        "Billing address": "Rechnungsadresse",
        "Shipping address": "Versandadresse"
    }
}
</i18n>

<script>

import _ from 'lodash'
import { adminClient as apolloClient } from '@/store/apollo';

import pinia from '@/pinia';
import { useSetupStore, useShopPurchaseStore } from '@/stores';

import PaginatedItems from '@/components/PaginatedItems';
import AddressCard from '@/components/AddressCard'

import MemberApi from '@/extensions/restrictedArea/store/api/member';

export default {
    name: 'Customer',
    data() {
        return {
            drawer: {
                loading: false,
                saving: false,
                visibility: false,
                error: false,
                scope: 'ex',
                key: 'shop_customer',
                member: undefined,
                membersPaginated: {
                    path: null,
                    variables: {
                        // order: 'id',
                        // direction: 'asc',
                        paginationSizes: [20, 50, 100],
                    },
                    filters: {},
                    pageInfo: {
                        total: 0,
                        perPage: 20,
                        currentPage: 1,
                        lastPage: 0,
                    },
                    allColumns: [],
                    activeColumns: [],
                    showFilters: true,
                    link: {},
                    items: [],
                },
                sortables: [
                    'id',
                    'last_name',
                    'first_name'
                ]
            },
        };
    },
    // filters: {
    //     timestamp2date,
    // },
    components: {
        AddressCard,
        PaginatedItems,
    },
    computed: {
        // id() {
        //     return _.get(this.form, 'id')
        // },
        hasDifferentAddresses() {
            return _.get(this.form, 'purchaser.hasDifferentAddresses')
        },
        requiresShipping() {
            return _.get(this.form, 'requires_shipping')
        },
        customer() {
            return _.get(this.form, 'purchaser.customer')
        },
        name() {
            const firstName = _.get(this.form, 'purchaser.first_name')
            const lastName = _.get(this.form, 'purchaser.last_name')

            return _.trim(`${firstName} ${lastName}`)
        },
        email() {
            return _.get(this.form, 'purchaser.email')
        },
        addresses() {
            let addresses = _.get(this.form, 'purchaser.addresses') || []
            if (this.hasDifferentAddresses === false && addresses.length > 1) addresses = [addresses[0]]
            return addresses
        },
        hasFilters() {
            return !_.isEmpty(this.filters);
        },
        filters() {
            let filters = _.cloneDeep(this.drawer.membersPaginated.filters);
            delete filters.is_customer;
            delete filters.cms;
            return filters;
        },
        sort() {
            let membersPaginated = _.cloneDeep(this.drawer.membersPaginated.variables);
            let order, direction;
            if (membersPaginated.order !== undefined && membersPaginated.order !== '') order = membersPaginated.order;
            else order = 'id';

            if (membersPaginated.direction !== undefined && membersPaginated.direction !== '') direction = membersPaginated.direction;
            else direction = 'desc';

            return { field: order, direction };
        },
        setupStore() {
            return useSetupStore();
        },
        activeCms() {
            return this.setupStore.activeCms;
        },
        enabledExtensions() {
            return this.setupStore.enabledExtensions;
        },
        debounce() {
            return this.setupStore.debounce;
        },
        shopPurchaseStore() {
            return useShopPurchaseStore(pinia);
        },
        form() {
            return this.shopPurchaseStore.form;
        },
        initialized() {
            return this.shopPurchaseStore.formInitialized;
        },
    },
    methods: {
        openCustomerDrawer() {
            this.drawer.visibility = true
            this.refetch()
        },

        refetch() {
            this.updateLoading(true);
            // always add the cms id to filters
            const cmsID = this.activeCms.id;
            this.defineFilterValue({ key: 'cms', value: { key: 'cms', match: cmsID }});
            this.defineFilterValue({ key: 'is_customer', value: { key: 'is_customer', match: true }});

            return MemberApi.fetchMembersPaginated({ scope: this.drawer.scope, type: this.drawer.key, ...this.drawer.membersPaginated }).then((data) => {
                this.addPaginatedMembersToDrawer(data);

                const activeColumnKeys = _.map(data.config.activeColumnKeys, 'key');
                this.defineActiveColumns(activeColumnKeys);
            })
                .then(() => this.updateLoading(false))
                .catch(err => {
                    const { graphQLErrors } = err

                    let code = 501;
                    let message = 'An error occurred!';

                    if (graphQLErrors) {
                        code = _.get(graphQLErrors, '0.code') || code;
                        message = _.get(graphQLErrors, '0.message') || message;
                    }

                    let notify = true;
                    switch (code) {
                        case 401:
                            notify = false;
                            this.error = { code, message: 'You are not authorized' };
                            break;
                        case 403:
                            notify = false;
                            this.error = { code, message: 'You are not allowed to be here' };
                            break;
                        // case 404:
                        //     break;
                        default:
                            this.error = true;
                            break;
                    }

                    if (notify) {
                        this.$notify.error({
                            title: this.$t(message),
                            message: this.$t('Person list could not be fetched.'),
                            iconClass: 'fas el-icon-fa-times',
                            position: 'bottom-left',
                            customClass: 'dark',
                            duration: 8000
                        });
                    }

                    this.updateLoading(false);

                    /* eslint no-console: ["error", { allow: ["error"] }] */
                    console.error(err);
                });
        },
        debouncedRefetch: _.debounce(function() {
            // function scope is needed here
            this.updateLoading(true);
            this.refetch();
        }, useSetupStore().debounce),

        toggleSelectedCustomer(item) {
            document.activeElement.blur()
            if (this.drawer.member && this.drawer.member.id === item.id) {
                this.drawer.member = undefined
            } else {
                this.drawer.member = item;
            }
        },

        updateLoading(loading) {
            this.drawer.loading = loading;
        },
        handleSizeChange(itemsPerPage) {
            this.updateLoading(true);
            this.defineItemsPerPage(itemsPerPage);
            this.refetch();
        },
        handleCurrentChange(currentPage) {
            this.updateLoading(true);
            this.defineCurrentPage(currentPage);
            this.refetch();
        },
        handleColsShow(visibleColumns) {
            this.defineActiveColumns(visibleColumns);
            // apolloClient.cache.reset()
            apolloClient.clearStore();
        },
        handleFilterInputChange(key, value, debounce = true) {
            this.defineFilterValue({ key, value });
            if (debounce) this.debouncedRefetch();
            else this.refetch();
        },
        handleSortInputChange(key, value) {
            this.defineSort({ key, value });
            this.refetch();
        },

        addPaginatedMembersToDrawer(data) {
            this.drawer.membersPaginated.items = data.items;
            this.drawer.membersPaginated.pageInfo = _.cloneDeep(data.pageInfo);

            this.drawer.membersPaginated.allColumns = _.cloneDeep(data.config.allColumns);

            this.drawer.membersPaginated.link = _.cloneDeep(data.config.link);
            this.drawer.membersPaginated.showFilters = _.get(data.config, 'showFilters') || false;
        },
        defineItemsPerPage(perPage) {
            this.drawer.membersPaginated.pageInfo.perPage = perPage;
        },
        defineCurrentPage(currentPage) {
            this.drawer.membersPaginated.pageInfo.currentPage = currentPage;
        },
        defineActiveColumns(activeColumnKeys) {
            let activeColumns = [];
            _.each(this.drawer.membersPaginated.allColumns, (column) => {
                let allowed = _.find(activeColumnKeys, (value) => column.key === value);
                if (allowed) activeColumns.push(column);
            });

            if (activeColumns.length) {
                this.drawer.membersPaginated.activeColumns = activeColumns;
            }
        },
        defineFilterValue({ key, value }) {
            if (value.match === '' && value.like === '' && value.from === '' && value.to === '' && value.in.length === 0) {
                this.$delete(this.drawer.membersPaginated.filters, key);
            } else {
                this.$set(this.drawer.membersPaginated.filters, key, value);
            }
        },
        defineSort({ key, value }) {
            this.$set(this.drawer.membersPaginated.variables, 'order', key);
            this.$set(this.drawer.membersPaginated.variables, 'direction', value);
        },

        savePurchaser() {
            this.drawer.saving = true

            let form = _.cloneDeep(this.form)
            form.purchaser = {
                id: form.purchaser.id,
                customer: this.drawer.member.customer
            }
            this.update(form)
                .then(() => {
                    this.drawer.saving = false
                    this.$notify.success({
                        title: this.$t('Customer saved successfully!'),
                        iconClass: 'fas el-icon-fa-check',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000
                    });
                    this.closeDrawer();
                })
                .catch(err => {
                    this.drawer.saving = false
                    this.$notify.error({
                        title: this.$t('An error occurred!'),
                        message: this.$t('Customer could not be saved.'),
                        iconClass: 'fas el-icon-fa-times',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000
                    });
                    /* eslint no-console: ["error", { allow: ["error"] }] */
                    console.error(err);
                })
        },
        closeDrawer() {
            this.drawer.visibility = false
        },
        resetDrawer() {
            this.drawer = {
                loading: false,
                saving: false,
                visibility: false,
                error: false,
                scope: 'ex',
                key: 'shop_customer',
                member: undefined,
                membersPaginated: {
                    path: null,
                    variables: {
                        // order: 'id',
                        // direction: 'asc',
                        paginationSizes: [20, 50, 100],
                    },
                    filters: {},
                    pageInfo: {
                        total: 0,
                        perPage: 20,
                        currentPage: 1,
                        lastPage: 0,
                    },
                    allColumns: [],
                    activeColumns: [],
                    showFilters: true,
                    link: {},
                    items: [],
                },
                sortables: [
                    'id',
                    'last_name',
                    'first_name'
                ]
            }
        },
        setMarker(address, latitude, longitude) {
            const i = _.findIndex(this.addresses, { id: address.id })
            this.setLatitude({ i, value: latitude })
            this.setLongitude({ i, value: longitude })
        },
        setLatitude(payload) {
            this.shopPurchaseStore.setFormLatitude(payload);
        },
        setLongitude(payload) {
            this.shopPurchaseStore.setFormLongitude(payload);
        },
        update(purchase) {
            return this.shopPurchaseStore.update(purchase);
        },
    },
};
</script>

<style lang="sass" scoped>
.flex:deep()
    .card
        margin-top: 20px
        +res(lg)
            margin-top: 0
            margin-left: 20px

.gray
    color: lighten($color-grey-text, 10%)
.title
    small
        font-size: 0.85rem
        position: relative
        top: -1px
.alert
    margin-top: 0

.purchaser-modal-form
    overflow-y: auto
    :deep()
        .table-bottom
            padding-bottom: 0
            min-height: 45px
        .actions-wrap .el-radio__label
            display: none
</style>
